import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Field, Modal, Notice, Select } from '../design/index.js'
import { useApi } from '../app/api.js'
import { mailTemplatesApi, printTemplatesApi } from '../app/templates-api.js'
import { messageFor } from './feedback.js'

/**
 * "Send by email" (PRN-002, PRN-027, PRN-047): which wording, and which
 * printed layout goes with it as the attachment. Both start on the defaults,
 * so sending the usual way is still one click.
 */
export const EmailDialog = ({ docType, documentId, onSent, onClose }: {
  docType: string
  documentId: string
  onSent: (result: { to: string; queued: boolean; attachment: string | null }) => void
  onClose: () => void
}) => {
  const api = useApi()
  const wordings = useQuery({ queryKey: ['mail-choices', api.companyId, docType], queryFn: () => mailTemplatesApi(api).choices(docType) })
  const layouts = useQuery({ queryKey: ['layouts', api.companyId, docType], queryFn: () => printTemplatesApi(api).choices(docType) })
  const capabilities = useQuery({ queryKey: ['mail-capabilities'], queryFn: () => mailTemplatesApi(api).capabilities() })
  const [mailTemplateId, setMail] = useState('')
  const [printTemplateId, setPrint] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { if (!mailTemplateId && wordings.data?.[0]) setMail(wordings.data[0].id) }, [wordings.data, mailTemplateId])
  useEffect(() => {
    const chosen = layouts.data?.find((l) => l.isDefault) ?? layouts.data?.[0]
    if (!printTemplateId && chosen) setPrint(chosen.id)
  }, [layouts.data, printTemplateId])

  const send = useMutation({
    mutationFn: () => mailTemplatesApi(api).send(documentId, {
      ...(mailTemplateId ? { mailTemplateId } : {}), ...(printTemplateId ? { printTemplateId } : {}),
    }),
    onSuccess: (result) => onSent(result),
    onError: (err) => setError(messageFor(err)),
  })

  return (
    <Modal
      title="Send by email"
      subtitle="To the party on the document, with the printed copy attached."
      onClose={onClose}
      actions={(
        <>
          <Button kind="ghost" onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={send.isPending || wordings.isLoading} onClick={() => send.mutate()}>Send</Button>
        </>
      )}
    >
      {error ? <Notice kind="bad">{error}</Notice> : null}
      <Field label="Email template">
        <Select value={mailTemplateId} onChange={setMail}
          options={(wordings.data ?? []).map((w) => ({ value: w.id, label: `${w.name}${w.isStandard ? ' (standard)' : ''}` }))} />
      </Field>
      <Field label="Attach the printed layout">
        <Select value={printTemplateId} onChange={setPrint}
          options={(layouts.data ?? []).map((l) => ({ value: l.id, label: `${l.name}${l.isDefault ? ' — default' : ''}` }))} />
      </Field>
      {capabilities.data && !capabilities.data.pdf ? (
        <p className="helper">This server does not make PDFs, so the printed document is attached as a web page.</p>
      ) : null}
    </Modal>
  )
}
